/**
 * In-memory previous-route tracker for Internal Ops dashboard tab landing.
 *
 * App.js calls recordPathname(location.pathname, location.search) during render (parent first),
 * so OpsDashboardTables can read the route the user came from before this
 * navigation's effects run.
 *
 * Tab rules:
 * - ?tab=transactions | rfqs | requests on /dashboard → that tab.
 * - Home (/dashboard with no tab) after a sidebar tab link → Transactions (index 0).
 * - From a row-details page (transaction / RFQ / request) → restore last tab.
 * - From any other route (sidebar Home, facilities, first visit) → Transactions (index 0).
 */

let previousPathname = '';
let currentPathname = '';
let previousSearch = '';
let currentSearch = '';

export const OPS_DASHBOARD_TAB_INDEX = {
    transactions: 0,
    rfqs: 1,
    requests: 2,
};

export const getOpsDashboardTabName = (index) => {
    const entry = Object.entries(OPS_DASHBOARD_TAB_INDEX).find(([, i]) => i === index);
    return entry ? entry[0] : 'transactions';
};

/**
 * Store the current path/search and shift the old one to previous.
 * No-ops on React re-renders of the same path+search so previous stays valid.
 */
export const recordPathname = (pathname, search = '') => {
    if (!pathname) {
        return;
    }
    const nextSearch = search || '';
    if (pathname === currentPathname && nextSearch === currentSearch) {
        return;
    }
    previousPathname = currentPathname;
    previousSearch = currentSearch;
    currentPathname = pathname;
    currentSearch = nextSearch;
};

/** Path the user was on before the latest recorded navigation. */
export const getPreviousPathname = () => previousPathname;

/** Search string the user had before the latest recorded navigation. */
export const getPreviousSearch = () => previousSearch;

/**
 * True when the previous page was a dashboard row-details screen.
 * Returning from these should keep RFQs / Requests selected instead of
 * resetting to Transactions.
 */
export const isOpsDashboardDetailRoute = (pathname) =>
    /^\/transactions\/[^/]+/.test(pathname) ||
    /^\/rfqs\/[^/]+/.test(pathname) ||
    pathname === '/requests' ||
    pathname.startsWith('/requests/');

export const getOpsDashboardTabFromSearch = (search = '') => {
    const normalized = search.startsWith('?') ? search.slice(1) : search;
    const tab = new URLSearchParams(normalized).get('tab');
    if (tab && tab in OPS_DASHBOARD_TAB_INDEX) {
        return tab;
    }
    return null;
};

const getStoredOpsDashboardTabIndex = () => {
    const storedTab = localStorage.getItem('opsDashboardTabIndex');
    const parsedTab = storedTab ? Number.parseInt(storedTab, 10) : 0;
    return parsedTab >= 0 && parsedTab <= 2 ? parsedTab : 0;
};

/**
 * Resolve which Ops Home dashboard tab to show.
 * Query `tab` wins; Home after a sidebar tab link resets to Transactions;
 * detail-route returns restore the last tab; everything else is Transactions.
 */
export const getOpsDashboardTabIndex = (search, previousPathnameValue, previousSearchValue = '') => {
    const tab = getOpsDashboardTabFromSearch(search);
    if (tab) {
        return OPS_DASHBOARD_TAB_INDEX[tab];
    }
    const previousTab = getOpsDashboardTabFromSearch(previousSearchValue);
    if (previousPathnameValue === '/dashboard' && previousTab) {
        return 0;
    }
    if (!isOpsDashboardDetailRoute(previousPathnameValue)) {
        return 0;
    }
    return getStoredOpsDashboardTabIndex();
};
